import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Stack } from '@rossum/ui/material';
import { MIN_FOOTER_HEIGHT } from '../../decorators/makeResizable/config';
import { DocumentCanvas } from '../../features/annotation-view/document-canvas-svg/DocumentCanvas';
import { useCanvasDimensions } from '../../features/annotation-view/document-canvas-svg/useCanvasDimensions';
import { DocumentSidebar } from '../../features/annotation-view/document-sidebar/DocumentSidebar';
import { resolveNoAccessReason } from '../../features/annotation-view/document-sidebar/no-access/resolveNoAccessReason';
import { AnnotationTopBar } from '../../features/annotation-view/document-topbar/AnnotationTopBar';
import {
  annotationSelector,
  pagesSelector,
} from '../../redux/modules/annotation/selectors';
import { currentMultivalueDatapointSelector } from '../../redux/modules/datapoints/selector';
import { DrawerConfig } from '../DocumentValidation/ValidationEmailDrawer';
import Footer from '../Footer/Footer';
import {
  getFittingHeightSelector,
  showFooterSelector,
} from '../Footer/selectors';
import { DocumentOverlayPanels } from './DocumentOverlayPanels';

type DocumentV2Props = {
  onEmailThreadOpen: (drawerConfig?: DrawerConfig) => void;
};

export const DocumentV2 = ({ onEmailThreadOpen }: DocumentV2Props) => {
  const initialHeight = useSelector(getFittingHeightSelector);

  const showFooter = useSelector(showFooterSelector);

  const currentMultivalueId = useSelector(
    currentMultivalueDatapointSelector
  )?.id;

  const pages = useSelector(pagesSelector);

  const dimensions = useCanvasDimensions(pages);

  const documentRef = useRef<HTMLDivElement>(null);

  // usePreventSwipeNavigation();
  // Reproduction steps
  // 1. Open a document that has many visible (not hidden) fields so the Annotation panel becomes scrollable.
  // 2. In the Annotation panel, click into any field input (e.g., a text field) to focus it.
  // 3. With the input still focused, attempt to scroll the Annotation panel (mouse wheel / trackpad / scrollbar drag).

  const annotation = useSelector(annotationSelector);

  const annotationLoaded = annotation && typeof annotation.id === 'number';

  const noAccessReason = useMemo(
    () => resolveNoAccessReason(annotation),
    [annotation]
  );

  return (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
      }}
    >
      <AnnotationTopBar loading={!annotationLoaded} />
      <Stack
        direction="row"
        sx={{ height: 'calc(100% - 48px)', width: '100%', flex: '1 0 auto' }}
      >
        <DocumentSidebar
          annotation={annotation}
          loading={!annotationLoaded}
          noAccessReason={noAccessReason}
          onEmailThreadOpen={onEmailThreadOpen}
        />
        <Stack
          sx={{ flex: '1 1 auto', position: 'relative', overflow: 'hidden' }}
          id="document"
        >
          <DocumentOverlayPanels dimensions={dimensions} />

          <Stack
            ref={documentRef}
            sx={{
              flex: '0 1 auto',
              alignItems: 'center',
              position: 'relative',
              overflow: 'hidden',
              marginBottom: '5px',
              height: '100%',
            }}
            data-tourstep="dataCaptureProductTour-document"
          >
            <DocumentCanvas
              pages={pages}
              data-tourstep="dataCaptureProductTour-missing-bbox"
              dimensions={dimensions}
            />
          </Stack>
          {showFooter && (
            <Footer
              resetKey={currentMultivalueId}
              minHeight={MIN_FOOTER_HEIGHT}
              height={initialHeight}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
